import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Plus, Trash2, Loader2, ExternalLink, Truck, Pencil } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { getStageGroup } from '@/lib/chargingStages';

interface LinkedLocation {
  id: string;
  location_id: string;
  interest_level: 'interested' | 'high_priority' | 'committed';
  notes: string | null;
  trucks_per_day: number | null;
  created_at: string;
  location: {
    id: string;
    name: string;
    city: string | null;
    stage: string;
    locode: string | null;
  };
}

interface ChargingLocation {
  id: string;
  name: string;
  city: string | null;
  stage: string;
  locode: string | null;
}

interface OpportunityLocationsSectionProps {
  opportunityId: string;
}

const INTEREST_LEVELS = [
  { id: 'interested', label: 'Geïnteresseerd', color: 'bg-blue-500' },
  { id: 'high_priority', label: 'Hoge prioriteit', color: 'bg-orange-500' },
  { id: 'committed', label: 'Commitment', color: 'bg-green-500' },
];

const STAGE_COLORS: Record<string, string> = {
  acquisitie: 'bg-blue-500',
  realisatie: 'bg-orange-500',
  operatie: 'bg-green-500',
};

export function OpportunityLocationsSection({ opportunityId }: OpportunityLocationsSectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [linkedLocations, setLinkedLocations] = useState<LinkedLocation[]>([]);
  const [allLocations, setAllLocations] = useState<ChargingLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    location_id: '',
    interest_level: 'interested' as 'interested' | 'high_priority' | 'committed',
    notes: '',
    trucks_per_day: null as number | null,
  });

  const isCrmPortal = location.pathname.startsWith('/crm');
  const locationBasePath = isCrmPortal ? '/crm/charging/locatie' : '/charging/locatie';

  useEffect(() => {
    fetchData();
  }, [opportunityId]);

  const fetchData = async () => {
    try {
      // Fetch linked locations
      const { data: linked, error: linkedError } = await supabase
        .from('opportunity_locations')
        .select(`
          id,
          location_id,
          interest_level,
          notes,
          trucks_per_day,
          created_at,
          location:charging_locations(id, name, city, stage, locode)
        `)
        .eq('opportunity_id', opportunityId)
        .order('created_at', { ascending: true });

      if (linkedError) throw linkedError;
      setLinkedLocations((linked || []) as LinkedLocation[]);

      // Fetch all available locations (excluding verloren)
      const { data: locations, error: locError } = await supabase
        .from('charging_locations')
        .select('id, name, city, stage, locode')
        .is('deleted_at', null)
        .order('locode');

      if (locError) throw locError;
      
      // Filter out verloren locations
      const filtered = (locations || []).filter((loc: any) => 
        getStageGroup(loc.stage) !== 'verloren'
      );
      setAllLocations(filtered);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ location_id: '', interest_level: 'interested', notes: '', trucks_per_day: null });
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (link: LinkedLocation) => {
    setFormData({
      location_id: link.location_id,
      interest_level: link.interest_level,
      notes: link.notes || '',
      trucks_per_day: link.trucks_per_day,
    });
    setEditingId(link.id);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!editingId && !formData.location_id) {
      toast({ title: 'Selecteer een locatie', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // Update existing
        const { error } = await supabase
          .from('opportunity_locations')
          .update({
            interest_level: formData.interest_level,
            notes: formData.notes || null,
            trucks_per_day: formData.trucks_per_day,
          })
          .eq('id', editingId);

        if (error) throw error;
        toast({ title: 'Koppeling bijgewerkt' });
      } else {
        // Insert new
        const { error } = await supabase
          .from('opportunity_locations')
          .insert({
            opportunity_id: opportunityId,
            location_id: formData.location_id,
            interest_level: formData.interest_level,
            notes: formData.notes || null,
            trucks_per_day: formData.trucks_per_day,
          });

        if (error) {
          if (error.code === '23505') {
            toast({ title: 'Deze locatie is al gekoppeld', variant: 'destructive' });
          } else {
            throw error;
          }
          return;
        }
        toast({ title: 'Locatie gekoppeld' });
      }

      resetForm();
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving location:', error);
      toast({ title: 'Fout bij opslaan', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLocation = async (linkId: string) => {
    try {
      const { error } = await supabase
        .from('opportunity_locations')
        .delete()
        .eq('id', linkId);

      if (error) throw error;
      
      toast({ title: 'Koppeling verwijderd' });
      fetchData();
    } catch (error) {
      console.error('Error removing location:', error);
      toast({ title: 'Fout bij verwijderen', variant: 'destructive' });
    }
  };

  // Available locations (not yet linked) - only for adding
  const availableLocations = allLocations.filter(
    loc => !linkedLocations.some(linked => linked.location_id === loc.id)
  );

  const getInterestBadge = (level: string) => {
    const config = INTEREST_LEVELS.find(l => l.id === level);
    return config ? (
      <Badge className={`${config.color} text-white`}>{config.label}</Badge>
    ) : null;
  };

  const getStageBadge = (stage: string) => {
    const group = getStageGroup(stage);
    const color = STAGE_COLORS[group] || 'bg-gray-500';
    return (
      <Badge variant="outline" className={`${color} text-white border-0`}>
        {group.charAt(0).toUpperCase() + group.slice(1)}
      </Badge>
    );
  };

  const formatLocationDisplay = (loc: ChargingLocation) => {
    const stage = getStageGroup(loc.stage);
    const stageLabel = stage.charAt(0).toUpperCase() + stage.slice(1);
    return `${loc.locode || '---'} - ${loc.name} (${stageLabel}) - ${loc.city || 'Onbekend'}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Geïnteresseerde Laadlocaties
          </CardTitle>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={openAddModal}
            disabled={availableLocations.length === 0}
          >
            <Plus className="h-4 w-4 mr-1" />
            Koppelen
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {linkedLocations.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nog geen laadlocaties gekoppeld
            </p>
          ) : (
            linkedLocations.map((link) => (
              <div key={link.id} className="flex items-start gap-3 p-2 rounded-lg bg-muted/50">
                <MapPin className="h-4 w-4 mt-1 text-primary" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="link"
                      className="p-0 h-auto font-medium"
                      onClick={() => navigate(`${locationBasePath}/${link.location.id}`)}
                    >
                      <span className="font-mono text-xs mr-1">{link.location.locode || '---'}</span>
                      {link.location.name}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {getStageBadge(link.location.stage)}
                    {getInterestBadge(link.interest_level)}
                    {link.trucks_per_day && (
                      <Badge variant="outline" className="gap-1">
                        <Truck className="h-3 w-3" />
                        {link.trucks_per_day}/dag
                      </Badge>
                    )}
                  </div>
                  {link.location.city && (
                    <p className="text-xs text-muted-foreground mt-1">{link.location.city}</p>
                  )}
                  {link.notes && (
                    <p className="text-xs text-muted-foreground mt-1 italic">{link.notes}</p>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => openEditModal(link)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => handleRemoveLocation(link.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) { resetForm(); setModalOpen(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Locatie bewerken' : 'Laadlocatie koppelen'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {!editingId && (
              <div>
                <Label>Laadlocatie *</Label>
                <Select
                  value={formData.location_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, location_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer locatie..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLocations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        <span className="font-mono text-xs">{loc.locode || '---'}</span>
                        <span className="mx-1">-</span>
                        <span>{loc.name}</span>
                        <span className="text-muted-foreground text-xs ml-1">
                          ({getStageGroup(loc.stage).charAt(0).toUpperCase() + getStageGroup(loc.stage).slice(1)})
                        </span>
                        <span className="text-muted-foreground text-xs ml-1">- {loc.city || 'Onbekend'}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {editingId && (
              <div>
                <Label>Laadlocatie</Label>
                <div className="p-2 bg-muted rounded text-sm">
                  {linkedLocations.find(l => l.id === editingId)?.location.name || 'Onbekend'}
                </div>
              </div>
            )}

            <div>
              <Label>Interesse niveau</Label>
              <Select
                value={formData.interest_level}
                onValueChange={(value: 'interested' | 'high_priority' | 'committed') => 
                  setFormData(prev => ({ ...prev, interest_level: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTEREST_LEVELS.map((level) => (
                    <SelectItem key={level.id} value={level.id}>
                      <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${level.color}`} />
                        <span>{level.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Vrachtwagens per dag</Label>
              <Input
                type="number"
                min={0}
                placeholder="0"
                value={formData.trucks_per_day || ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  trucks_per_day: e.target.value ? parseInt(e.target.value) : null 
                }))}
              />
            </div>

            <div>
              <Label>Notitie (optioneel)</Label>
              <Textarea
                placeholder="Eventuele opmerkingen..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setModalOpen(false); }}>
              Annuleren
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || (!editingId && !formData.location_id)}
            >
              {saving ? 'Opslaan...' : editingId ? 'Bijwerken' : 'Koppelen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
